import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DotsThreeVertical, PencilSimple, ShareNetwork, Trash, ArrowSquareOut, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PropertyEditModal from "@/components/PropertyEditModal";
import DeletePropertyReasonModal from "@/components/DeletePropertyReasonModal";
import QuickShareSheet from "@/components/QuickShareSheet";
import { getShareUrl } from "@/lib/shareUrl";

/**
 * PropertyCardActions — menu contestuale "Modifica · Condividi · Elimina"
 * sulla card immobile.
 *
 * Mobile: long-press (550ms) sulla card apre il menu.
 * Desktop: icona "···" in alto a destra appare on-hover.
 *
 * Iter 271 (round 2): l'utente da iPhone vuole modificare/eliminare senza
 * dover entrare dentro la scheda e ri-uscire.
 *
 * Usage:
 *   <Link>
 *     <PropertyCardActions property={p} onChanged={reload} />
 *     ...card content...
 *   </Link>
 *
 * Il componente intercetta il click sulla zona icona/menu e fa `stopPropagation`
 * per non navigare alla detail page.
 */
export default function PropertyCardActions({ property, onChanged }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [quickShareOpen, setQuickShareOpen] = useState(false);
  const longPressTimer = useRef(null);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  // Setup long-press detection (mobile) — montato sul PARENT <Link> via DOM
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    // L'elemento da osservare è il parent Link (closest a)
    const card = wrapper.closest("a[data-testid^='property-card-']");
    if (!card) return;

    const onPointerDown = (e) => {
      // Solo touch / pen (no mouse click destro)
      if (e.pointerType === "mouse") return;
      longPressTimer.current = setTimeout(() => {
        if (typeof navigator?.vibrate === "function") {
          try { navigator.vibrate(50); } catch (_) { /* silent */ }
        }
        setMenuOpen(true);
        // Previene la navigazione: cancella il tap che segue
        try { card.style.pointerEvents = "none"; } catch (_) {}
        setTimeout(() => { try { card.style.pointerEvents = ""; } catch (_) {} }, 600);
      }, 550);
    };
    const cancel = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    };
    card.addEventListener("pointerdown", onPointerDown);
    card.addEventListener("pointerup", cancel);
    card.addEventListener("pointercancel", cancel);
    card.addEventListener("pointerleave", cancel);
    card.addEventListener("contextmenu", (e) => {
      // Su mobile il long-press triggera anche il context menu nativo: blocchiamolo.
      if (longPressTimer.current === null) e.preventDefault();
    });
    return () => {
      card.removeEventListener("pointerdown", onPointerDown);
      card.removeEventListener("pointerup", cancel);
      card.removeEventListener("pointercancel", cancel);
      card.removeEventListener("pointerleave", cancel);
    };
  }, []);

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const onShareClick = useCallback(async (e) => {
    stop(e);
    setMenuOpen(false);
    try {
      // Ottieni o crea listing
      let listing = await api.get(`/properties/${property.id}/listing`).then((r) => r.data).catch(() => null);
      if (!listing?.exists) {
        await api.post(`/properties/${property.id}/listing`);
        listing = await api.get(`/properties/${property.id}/listing`).then((r) => r.data);
      }
      // iter 313 r9 — usiamo `/api/share/{id}` invece di `/listing/{id}` così
      // WhatsApp/Telegram/iMessage ricevono i meta OG (titolo+foto+descrizione)
      // e mostrano il preview ricco nel chat. Il browser umano viene rediretto
      // automaticamente alla SPA.
      const url = getShareUrl(listing.public_id);
      if (navigator.share) {
        // iter v32 r30 — Formale (Lei) + saluto in base all'orario per
        // coerenza con il banner "Settimana sto presentando…".
        const hourIt = new Date().getHours();
        const saluto = hourIt < 18 ? "Buongiorno" : "Buonasera";
        await navigator.share({
          title: property.title || "Immobile",
          text: `${saluto}, Le mando in anteprima: ${property.title || "questo immobile"}`,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiato negli appunti");
      }
    } catch (err) {
      // L'utente potrebbe aver chiuso il share sheet — silent
      if (err?.name !== "AbortError") toast.error("Condivisione non riuscita");
    }
  }, [property]);

  const onDeleteConfirm = useCallback(async (payload) => {
    // iter v28 r22 — Bug fix: il modal chiama onConfirm(payload) con un OGGETTO
    // ({reason, final_price_eur, closed_at, note}). Prima questa fn lo prendeva
    // come `reason` (string) e poi ri-wrappava in { data: { reason: payload } }
    // → backend Pydantic 422 → toast "Eliminazione non riuscita". Ora lo
    // inoltro come body intero, come fa già PropertyDetail::handleDeleteConfirm.
    try {
      const hasReason = payload && payload.reason;
      await api.delete(`/properties/${property.id}`, hasReason ? { data: payload } : undefined);
      toast.success(
        payload?.reason === "sold"
          ? "Vendita registrata. Sarà nel tuo Wrapped annuale."
          : "Immobile eliminato",
      );
      setDeleteOpen(false);
      // Cleanup cache (vedi sotto per logica zombie card)
      try {
        const cached = JSON.parse(localStorage.getItem("pronto_properties_cache") || "[]");
        const filtered = Array.isArray(cached) ? cached.filter((p) => p?.id !== property.id) : [];
        localStorage.setItem("pronto_properties_cache", JSON.stringify(filtered));
      } catch { /* noop */ }
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      // iter v28 r393 — User M1302 "zombie card": se l'utente clicca
      // "Elimina" su una card che è già stata eliminata altrove (cache
      // localStorage stale), il backend ritorna 404. Trattiamo il 404 come
      // "ok, era già cancellato" + invalidiamo la cache + ricarichiamo la
      // lista, così la zombie card sparisce subito invece di mostrare un
      // toast d'errore confuso ("Immobile non trovato").
      if (err?.response?.status === 404) {
        toast.success("Immobile già eliminato. Aggiorno la lista.");
        setDeleteOpen(false);
        try {
          const cached = JSON.parse(localStorage.getItem("pronto_properties_cache") || "[]");
          const filtered = Array.isArray(cached) ? cached.filter((p) => p?.id !== property.id) : [];
          localStorage.setItem("pronto_properties_cache", JSON.stringify(filtered));
        } catch { /* noop */ }
        if (typeof onChanged === "function") onChanged();
        return;
      }
      const msg = err?.response?.data?.detail || "Eliminazione non riuscita";
      toast.error(typeof msg === "string" ? msg : "Eliminazione non riuscita");
    }
  }, [property, onChanged]);

  const onPublicOpen = useCallback(async (e) => {
    stop(e);
    setMenuOpen(false);
    try {
      const listing = await api.get(`/properties/${property.id}/listing`).then((r) => r.data).catch(() => null);
      if (listing?.exists && listing?.is_public !== false && listing?.public_id) {
        window.open(`/listing/${listing.public_id}`, "_blank");
      } else {
        navigate(`/dashboard/property/${property.id}?tab=condivisione`);
      }
    } catch (_) {
      navigate(`/dashboard/property/${property.id}`);
    }
  }, [property, navigate]);

  return (
    <div ref={wrapperRef} className="contents">
      {/* Kebab icon — iter 275: SEMPRE visibile su mobile (era invisibile e
          il long-press non era scopribile/affidabile su iOS Safari).
          Su desktop appare on-hover come prima. */}
      <button
        type="button"
        onClick={(e) => { stop(e); setMenuOpen((v) => !v); }}
        className="absolute top-1.5 right-1.5 z-20 w-9 h-9 md:w-8 md:h-8 rounded-full bg-white/95 hover:bg-white border border-[#EAE7DF] shadow-sm inline-flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 transition-opacity"
        aria-label="Azioni · modifica o elimina"
        data-testid={`card-actions-kebab-${property.id}`}
      >
        <DotsThreeVertical size={18} weight="bold" />
      </button>

      {/* Menu */}
      {menuOpen && (
        <>
          {/* Backdrop solo su mobile per chiudere tap-fuori */}
          <button
            type="button"
            onClick={(e) => { stop(e); setMenuOpen(false); }}
            className="fixed inset-0 z-40 bg-black/30 md:bg-transparent"
            aria-label="Chiudi"
            data-testid={`card-actions-backdrop-${property.id}`}
          />
          <div
            onClick={stop}
            className="absolute top-12 right-1.5 z-50 w-52 bg-white rounded-xl shadow-lg border border-[#EAE7DF] overflow-hidden md:top-10"
            data-testid={`card-actions-menu-${property.id}`}
          >
            <button
              type="button"
              onClick={(e) => { stop(e); setMenuOpen(false); setEditOpen(true); }}
              className="w-full text-left px-3.5 py-2.5 text-sm font-medium text-[#1C1C1A] hover:bg-[#F1F4ED] flex items-center gap-2.5"
              data-testid={`card-actions-edit-${property.id}`}
            >
              <PencilSimple size={15} weight="bold" className="text-[#7C8B6E]" /> Modifica dati
            </button>
            {/* iter v30 r136 — Quick Share: scorciatoia per generare post/reel/
                carosello/story/occasioni speciali in 2 tap, senza entrare
                nell'editor social. Riusa gli stessi endpoint backend del
                Social Studio. */}
            <button
              type="button"
              onClick={(e) => { stop(e); setMenuOpen(false); setQuickShareOpen(true); }}
              className="w-full text-left px-3.5 py-2.5 text-sm font-medium text-[#1C1C1A] hover:bg-[#F1F4ED] flex items-center gap-2.5"
              data-testid={`card-actions-quick-share-${property.id}`}
            >
              <Sparkle size={15} weight="bold" className="text-[#7C8B6E]" /> Crea post
            </button>
            <button
              type="button"
              onClick={onPublicOpen}
              className="w-full text-left px-3.5 py-2.5 text-sm font-medium text-[#1C1C1A] hover:bg-[#F1F4ED] flex items-center gap-2.5"
              data-testid={`card-actions-public-${property.id}`}
            >
              <ArrowSquareOut size={15} weight="bold" className="text-[#7C8B6E]" /> Apri pagina pubblica
            </button>
            <button
              type="button"
              onClick={onShareClick}
              className="w-full text-left px-3.5 py-2.5 text-sm font-medium text-[#1C1C1A] hover:bg-[#F1F4ED] flex items-center gap-2.5"
              data-testid={`card-actions-share-${property.id}`}
            >
              <ShareNetwork size={15} weight="bold" className="text-[#7C8B6E]" /> Condividi link
            </button>
            <div className="h-px bg-[#F0EFE9]" />
            <button
              type="button"
              onClick={(e) => { stop(e); setMenuOpen(false); setDeleteOpen(true); }}
              className="w-full text-left px-3.5 py-2.5 text-sm font-medium text-[#C66060] hover:bg-red-50 flex items-center gap-2.5"
              data-testid={`card-actions-delete-${property.id}`}
            >
              <Trash size={15} weight="bold" /> Elimina
            </button>
          </div>
        </>
      )}

      {editOpen && (
        <PropertyEditModal
          open={editOpen}
          property={property}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); if (typeof onChanged === "function") onChanged(); }}
        />
      )}

      <DeletePropertyReasonModal
        open={deleteOpen}
        title={property?.title}
        askingPrice={property?.price}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onDeleteConfirm}
      />

      <QuickShareSheet
        open={quickShareOpen}
        property={property}
        onClose={() => setQuickShareOpen(false)}
      />
    </div>
  );
}
